import { Button, Menu, Popover, Stack, Text, TextInput } from "@mantine/core";
import { useRef, useState } from "react";

import { useProductAnalytics } from "../../shared/analytics/useProductAnalytics.js";
import { canonicalCustomerWorkspaceUrl } from "./customerWorkspaceLink.js";

export interface ShareLinkButtonProps {
  readonly asMenuItem?: boolean;
  readonly analyticsSurface?: "comparison" | "detail" | "report";
  readonly className?: string;
  readonly compactLabel?: string;
  readonly label?: string;
  readonly onActivate?: () => void;
  readonly onResult?: (result: ShareLinkResult) => void;
  readonly renderFeedback?: boolean;
  readonly url?: string;
}

type ShareStatus = "idle" | "copied" | "fallback" | "invalid";
export type ShareLinkResult = Readonly<{
  status: Exclude<ShareStatus, "idle">;
  url: string;
}>;

function canonicalAbsoluteUrl(url: string | undefined): string {
  const target = canonicalCustomerWorkspaceUrl(
    url ?? window.location.href,
    window.location.origin,
  );
  if (target === null) throw new Error("Invalid customer link");
  return target;
}

export function ShareLinkButton({
  asMenuItem = false,
  analyticsSurface = "report",
  className,
  compactLabel,
  label = "Share",
  onActivate,
  onResult,
  renderFeedback = true,
  url,
}: ShareLinkButtonProps) {
  const analytics = useProductAnalytics();
  const [fallbackUrl, setFallbackUrl] = useState("");
  const [status, setStatus] = useState<ShareStatus>("idle");
  const actionRef = useRef<HTMLButtonElement>(null);
  const fallbackInputRef = useRef<HTMLInputElement>(null);

  async function share() {
    onActivate?.();
    if (renderFeedback) setStatus("idle");
    let canonicalUrl: string;
    try {
      canonicalUrl = canonicalAbsoluteUrl(url);
    } catch {
      if (renderFeedback) {
        setFallbackUrl("");
        setStatus("invalid");
      }
      onResult?.({ status: "invalid", url: "" });
      return;
    }
    if (renderFeedback) setFallbackUrl(canonicalUrl);
    try {
      if (typeof navigator.clipboard?.writeText !== "function") {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(canonicalUrl);
      analytics.reportInteraction({
        action: "copied",
        feature: "share",
        surface: analyticsSurface,
      });
      if (renderFeedback) setStatus("copied");
      onResult?.({ status: "copied", url: canonicalUrl });
    } catch {
      if (renderFeedback) setStatus("fallback");
      onResult?.({ status: "fallback", url: canonicalUrl });
    }
  }

  const announcement = status === "copied"
    ? "Link copied."
    : status === "fallback"
      ? "Clipboard unavailable. Copy the link below."
      : status === "invalid"
        ? "Link unavailable."
      : "";
  const fallbackOpened = status === "fallback" || status === "invalid";
  const content = (
    <>
      <span className={compactLabel ? "responsive-action-label__full" : undefined}>{label}</span>
      {compactLabel ? (
        <span aria-hidden="true" className="responsive-action-label__compact">
          {compactLabel}
        </span>
      ) : null}
    </>
  );
  const action = asMenuItem ? (
    <Menu.Item aria-label={label} onClick={() => void share()} ref={actionRef}>{content}</Menu.Item>
  ) : (
    <Button
      aria-label={label}
      className={className}
      onClick={() => void share()}
      ref={actionRef}
      size="xs"
      variant="light"
    >
      {content}
    </Button>
  );

  if (!renderFeedback) return action;

  return (
    <>
      <Popover
        onEnterTransitionEnd={() => fallbackInputRef.current?.focus()}
        onChange={(opened) => {
          if (!opened) {
            setStatus("idle");
          }
        }}
        opened={fallbackOpened}
        position="bottom-end"
        returnFocus
        withArrow
      >
        <Popover.Target>{action}</Popover.Target>
        <Popover.Dropdown>
          <Stack className="share-link-fallback" gap="xs">
            {status === "invalid" ? (
              <Text size="sm">This link could not be prepared.</Text>
            ) : (
              <>
                <Text size="sm">Clipboard access is unavailable. Copy this link:</Text>
                <TextInput
                  aria-label="Copy this link"
                  autoFocus
                  onFocus={(event) => event.currentTarget.select()}
                  readOnly
                  ref={fallbackInputRef}
                  value={fallbackUrl}
                />
              </>
            )}
          </Stack>
        </Popover.Dropdown>
      </Popover>
      <span aria-live="polite" className="visually-hidden" role="status">
        {announcement}
      </span>
    </>
  );
}
